import { InvoicePosition, InvoicePositionDto } from '@shared/models/invoice-position';
import { BookingDialogService } from '@sharedComponents/dialogs/booking-dialog/services/booking-dialog.service';
import { BookingDialogBaseEntity } from '@sharedComponents/dialogs/booking-dialog/services/classes/abstract/booking-dialog-base-entity';
import { TenantRelationAssignment } from '@shared/models/tenant-relation-assignment';
import { BerthBoatAssignment } from '@shared/models/berth-boat-assignment';
import { ElectricMeterAssignment } from '@shared/models/electric-meter-assignment';
import { TolgeeService } from '@core/translation/tolgee.service';
import { DisconnectElectricMeterEvent } from '@sharedComponents/dialogs/booking-dialog/tabs/electric-meter-tab/electric-meter/assignment-electric-meters.component';
import { toDateStringDE } from '@core/functions/to-date-string';
import { ProductService } from '@modules/bcm/products/product/product.service';
import { Product } from '@shared/models/product';
import { UnitUniqueName } from '@shared/models/unit';
import { concatMap, forkJoin, from, Observable, of, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { BcmDynamicPrice } from '@shared/models/bcm-dynamic-price';
import { BookingAttribute } from '@sharedComponents/dialogs/booking-dialog/enums/booking-attribute.enum';
import { BcmBookingsApiService } from '@bcmApiServices/bcm-bookings.api-service';
import { BcmSettingsSectionName, DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { calculateProportionalTime } from '@shared/functions/calculate-proportional-time';
import { DurationUnit, getDurationFromDates } from '@shared/functions/get-duration-from-dates';
import { ElectricMeterProductKey } from '@shared/models/electric-meter-product-type';
import {
    filterBerthProductsForBooking,
    filterPositionsForSave,
    filterTenantRelationPositionsForSave,
    isDuplicateBerthBookingPosition,
    resolveBerthProductQuantity,
    resolveBerthProductQuantityForBooking,
} from '@shared/utils/berth-product-booking.util';
import { DEFAULT_BERTH_RESERVATION_UNIT } from '@modules/bcm/settings/default-units/default-units.component';
import { hasUnmatchedPriceRules } from '@shared/functions/resolve-dynamic-price-state';

export class BookingDialogPositions extends BookingDialogBaseEntity<InvoicePosition, InvoicePosition[]> {

    constructor(
        bookingDialogService: BookingDialogService,
        private _bookingsApiService: BcmBookingsApiService,
        private _productService: ProductService,
        private _bcmSettingsFacade: BcmSettingsFacade,
    ) {
        super(bookingDialogService, BookingAttribute.POSITIONS);

        this._defaultBerthReservationTimeUnit = this._bcmSettingsFacade.settings()[BcmSettingsSectionName.DefaultBerthReservationTimeUnit];
    }

    private readonly _defaultBerthReservationTimeUnit: DefaultBerthReservationTimeUnit;

    /** Produkte, deren Beziehungsposition wegen einer nicht greifenden Preisregel unterblieben ist. */
    private readonly _unmatchedTenantRelationProductIds = new Set<number>();

    protected isDuplicate(value: InvoicePosition): boolean {
        return this.bookingDialogService.positions.value.some(sub => {
            if (value.fromBerth) {
                return isDuplicateBerthBookingPosition(sub, value);
            }

            return sub.product.id === value.product.id &&
                sub.quantity === value.quantity &&
                sub.fromTenantRelation &&
                !sub.id;
        });
    }

    getPositionsForSave(): InvoicePosition[] {
        const enriched = this.value.map(position => this._enrichBerthPositionFlags(position));

        return filterTenantRelationPositionsForSave(filterPositionsForSave(enriched));
    }

    private _enrichBerthPositionFlags(position: InvoicePosition): InvoicePosition {
        const berthAssignment = this._findBerthAssignmentForPosition(position);

        if (!berthAssignment?.berth?.products?.length) {
            return position;
        }

        const berthProduct = berthAssignment.berth.products.find(product => product.id === position.product?.id);

        if (!berthProduct) {
            return position;
        }

        position.fromBerth = true;
        position.product.ignoreFreeOfChargePosition = berthProduct.ignoreFreeOfChargePosition;
        position.product.skipStayDurationQuantity = berthProduct.skipStayDurationQuantity;

        return position;
    }

    private _getDurationOfStay(assignment: BerthBoatAssignment): number {
        if (assignment.durationOfStay > 0) {
            return assignment.durationOfStay;
        }

        const durationUnit = this._defaultBerthReservationTimeUnit?.type === DEFAULT_BERTH_RESERVATION_UNIT.OVERNIGHT_STAY
            ? DurationUnit.Overnights
            : DurationUnit.Days;

        return getDurationFromDates(
            assignment.from,
            assignment.to,
            durationUnit,
            this._defaultBerthReservationTimeUnit,
        );
    }

    private _upsertBerthPosition(position: InvoicePosition): void {
        const existingIndex = this.value.findIndex(sub => isDuplicateBerthBookingPosition(sub, position));

        if (existingIndex === -1) {
            this.add(position);
            return;
        }

        const updated = [...this.value];
        const existing = updated[existingIndex];

        const merged = new InvoicePosition({
            ...(existing as unknown as InvoicePositionDto),
            ...(position as unknown as InvoicePositionDto),
            id: existing.id,
        });

        // the constructor always generates a new uuid, so keep the existing one
        merged.uuid = existing.uuid;

        updated[existingIndex] = merged;

        this.value = updated;
    }

    private _findBerthAssignmentForPosition(position: InvoicePosition) {
        if (position.berthAssignmentUuid) {
            return this.bookingDialogService.berthAssignments.value
                .find(assignment => assignment.uuid === position.berthAssignmentUuid);
        }

        if (position.bcm_berth_has_assignments_id) {
            return this.bookingDialogService.berthAssignments.value
                .find(assignment => assignment.id === position.bcm_berth_has_assignments_id);
        }

        return null;
    }

    reload(): void {
        this._bookingsApiService.getPositions(this.bookingDialogService.booking.id)
            .subscribe(positions => {

                positions.forEach(p => {
                    if (p.bcm_berth_has_assignments_id) {
                        p.berthAssignmentUuid = this.bookingDialogService.berthAssignments.value.find(a => a.id === p.bcm_berth_has_assignments_id)?.uuid;
                    }
                    if (p.bcm_electric_meter_has_readings_id) {
                        p.electricMeterAssignmentUuid = this.bookingDialogService.electricMeterAssignments.value.find(a => a.id === p.bcm_electric_meter_has_boats_id)?.uuid;
                    }
                    return p;
                });

                this.bookingDialogService.booking.positions = positions;
                this.value = positions;
            });
    }

    /**
     * `keepUnsavedChanges`: für Zeilen, die nur wieder eingeblendet werden (Anzahl-0-Produkte einer
     * gespeicherten Buchung, #7140) – bloßes Öffnen darf nicht als Änderung zählen.
     */
    addFromTenantRelation(assignment: TenantRelationAssignment, products?: Product[], keepUnsavedChanges = false): void {

        if (this.bookingDialogService.readonlyView) {
            return;
        }

        const positions = this._buildTenantRelationPositions(assignment, products);

        if (!positions.length) {
            return;
        }

        from(positions).pipe(
            concatMap(position => this.getPositionWithDynamicPrice(position))
        ).subscribe({
            next: dynamicPricePosition => {
                const product = dynamicPricePosition.product;

                // Ohne Regeltreffer liefert der Evaluator den Grundpreis des Produkts zurück – die
                // Zeile sah damit wie eine ganz normale Leistung aus und wurde mitberechnet. Zwei
                // Beziehungsprodukte "bis 6 m"/"über 6 m" landeten so beide unter "Leistungen",
                // obwohl nur eines auf das Boot passt. (#7017)
                if (product?.hasDynamicPrice
                    && hasUnmatchedPriceRules(product, dynamicPricePosition.dynamicPrice)) {
                    this._unmatchedTenantRelationProductIds.add(product.id);
                    return;
                }

                this._unmatchedTenantRelationProductIds.delete(product?.id);

                const unsavedChanges = this.bookingDialogService.unsavedChanges;

                this.add(dynamicPricePosition);

                if (keepUnsavedChanges) {
                    this.bookingDialogService.unsavedChanges = unsavedChanges;
                }
            }
        });

    }

    /**
     * Wertet die dyn. Preise der Beziehungsprodukte aus, ohne etwas an den Positionen zu ändern –
     * für die Preisvorschau in der "Produkte gefunden"-Box. Läuft bewusst über dieselben
     * Wegwerf-Positionen und dasselbe getPositionWithDynamicPrice() wie das spätere Anlegen der
     * Leistungen, damit Box und Reiter "Leistungen" nicht auseinanderlaufen können.
     */
    evaluateTenantRelationProductPrices(
        assignment: TenantRelationAssignment,
        products?: Product[],
    ): Observable<Map<number, BcmDynamicPrice>> {

        const positions = this._buildTenantRelationPositions(assignment, products)
            .filter(position => position.product?.hasDynamicPrice);

        if (!positions.length) {
            return of(new Map<number, BcmDynamicPrice>());
        }

        return forkJoin(positions.map(position => this.getPositionWithDynamicPrice(position)))
            .pipe(
                map(evaluatedPositions => new Map<number, BcmDynamicPrice>(
                    evaluatedPositions
                        .filter(position => !!position.dynamicPrice)
                        .map(position => [position.product.id, position.dynamicPrice as BcmDynamicPrice])
                ))
            );
    }

    /**
     * Zieht die bereits angelegten Beziehungspositionen auf eine neue Auswertung nach – die Box
     * wertet bei jedem Kontextwechsel (Beziehung, Boot, Reisende) neu aus, die Positionen wurden
     * dagegen einmalig beim Anlegen bepreist und behielten z.B. nach der Bootwahl den alten Preis.
     *
     * Angefasst werden nur ungespeicherte Beziehungspositionen, deren Preis noch dem zuletzt
     * ausgewerteten Regelpreis entspricht: ein von Hand geänderter Preis bleibt stehen. (#6922)
     *
     * Dieselbe Auswertung entscheidet auch über das Dasein der Position: die Regeln hängen am Boot,
     * und das wird im Kartenfall erst nach der Beziehung gewählt – ohne Boot greift keine Regel.
     * Fällt ein Produkt jetzt durch, wird seine Position entfernt, greift seine Regel wieder, kommt
     * sie zurück. (#7017)
     */
    resyncTenantRelationPrices(
        assignment: TenantRelationAssignment,
        dynamicPrices: Map<number, BcmDynamicPrice>,
    ): void {

        if (this.bookingDialogService.readonlyView || !dynamicPrices.size) {
            return;
        }

        let changed = this._dropUnmatchedTenantRelationPositions(dynamicPrices);

        this.value
            .filter(position => position.fromTenantRelation && !position.id && !position.deleted)
            .forEach(position => {
                const dynamicPrice = dynamicPrices.get(position.product?.id);

                if (!dynamicPrice || position.price !== position.dynamicPrice?.rulePrice) {
                    return;
                }

                // Gegen den Titel prüfen, der wirklich gesetzt würde: bei einer von Hand
                // vergebenen Bezeichnung ist das ihre eigene, sonst wäre die Abbruchbedingung nie
                // erfüllt und jeder Kontextwechsel meldete eine ungespeicherte Änderung. (#6990)
                const title = this._keepsOwnTitle(position, position.dynamicPrice?.ruleName)
                    ? position.title
                    : (dynamicPrice.ruleName || position.product?.name);

                if (position.price === dynamicPrice.rulePrice && position.title === title) {
                    return;
                }

                position.dynamicPrice = dynamicPrice;
                position.price = dynamicPrice.rulePrice;
                position.title = title;

                changed = true;
            });

        if (changed) {
            this.sendUpdate(true);
        }

        this._restoreMatchedTenantRelationPositions(assignment, dynamicPrices);
    }

    /**
     * Entfernt die ungespeicherten Beziehungspositionen, deren Regel in der neuen Auswertung nicht
     * mehr greift, und merkt sich die Produkte für _restoreMatchedTenantRelationPositions().
     *
     * Ein von Hand geänderter Preis schützt die Zeile, wie er sie auch vor dem Preis-Nachzug schützt
     * (#6922): wer selbst einen Betrag eingetragen hat, will ihn abrechnen – ihn wortlos zu
     * entfernen wäre schlimmer als die Verwechslungsgefahr, um die es hier geht.
     */
    private _dropUnmatchedTenantRelationPositions(dynamicPrices: Map<number, BcmDynamicPrice>): boolean {

        const dropped = this.value.filter(position =>
            position.fromTenantRelation
            && !position.id
            && !position.deleted
            && position.price === position.dynamicPrice?.rulePrice
            && dynamicPrices.has(position.product?.id)
            && hasUnmatchedPriceRules(position.product, dynamicPrices.get(position.product.id)));

        if (!dropped.length) {
            return false;
        }

        dropped.forEach(position => this._unmatchedTenantRelationProductIds.add(position.product.id));

        this.value = this.value.filter(position => !dropped.includes(position));

        return true;
    }

    /**
     * Legt die Positionen der Produkte wieder an, die zuvor an ihrer Regel gescheitert sind und
     * jetzt wieder greifen – typischerweise nach der Bootwahl.
     *
     * Bewusst nur aus dieser Merkliste heraus: eine von Hand gelöschte Beziehungsposition darf beim
     * nächsten Kontextwechsel nicht wieder auftauchen.
     */
    private _restoreMatchedTenantRelationPositions(
        assignment: TenantRelationAssignment,
        dynamicPrices: Map<number, BcmDynamicPrice>,
    ): void {

        if (!this._unmatchedTenantRelationProductIds.size || !assignment?.toDate) {
            return;
        }

        const products = (assignment.tenantRelation?.products || []).filter(product =>
            this._unmatchedTenantRelationProductIds.has(product.id)
            && dynamicPrices.has(product.id)
            && !hasUnmatchedPriceRules(product, dynamicPrices.get(product.id)));

        if (products.length) {
            this.addFromTenantRelation(assignment, products);
        }
    }

    /**
     * Anzahl einer Beziehungsposition. Produkte mit Einheit Tag/Übernachtung werden mit der
     * Aufenthaltsdauer bemengt – wie Liegeplatzprodukte und wie die Handeingabe im
     * Bierdeckel-Dialog. Bisher zählte allein die an der Beziehung hinterlegte Stammdaten-Menge:
     * bei 0 blieb die Position dauerhaft auf 0, sonst wich sie von der in der blauen Box
     * gezeigten Übernachtungszahl ab. Jede andere Einheit behält Menge × Proportionalzeit. (#7017)
     *
     * Öffentlich, weil die Box "Produkte gefunden" dieselbe Methode aufruft – Box und Reiter
     * "Leistungen" dürfen nicht auseinanderlaufen (vgl. evaluateTenantRelationProductPrices).
     */
    resolveTenantRelationQuantity(assignment: TenantRelationAssignment, product: Product): number {

        // calculateProportionalTime dereferenziert payableOption.id ungeguardet – eine Beziehung ohne
        // Zahlweise würde sonst das Anlegen aller Positionen abbrechen.
        const payableOption = assignment?.tenantRelation?.payableOption ?? assignment?.payableOption;
        const proportionalTime = payableOption
            ? (calculateProportionalTime(assignment.fromDate, assignment.toDate, payableOption) ?? 1)
            : 1;

        return resolveBerthProductQuantity(
            product.quantity * proportionalTime,
            this._getTenantRelationDurationOfStay(assignment),
            product.unit?.uniqueName,
            // Fest false: skipStayDurationQuantity ist eine Spalte des Liegeplatz-Pivots, nicht des
            // Produkts. _enrichBerthPositionFlags() schreibt sie am selben Product-Objekt fest –
            // durchgereicht würde die Einstellung eines Liegeplatzes in die Beziehung lecken.
            false,
        );
    }

    /**
     * Aufenthaltsdauer der Belegung in der Einheit, die der Hafen eingestellt hat – dieselbe Weiche
     * wie _getDurationOfStay(), und dieselben Daten, aus denen auch die Proportionalzeit entsteht.
     *
     * Bewusst NICHT der Leistungszeitraum: der ist der Abrechnungszeitraum und wird von der
     * Saison-Auswahl bzw. getVestingPeriodUntil() auf ein volles Intervall gezogen – eine
     * Saisonbeziehung hätte damit hunderte "Übernachtungen" statt der gebuchten. Ohne Enddatum
     * (Abo-Fall) liefert getDurationFromDates() null und die Position fällt auf die
     * Stammdaten-Menge zurück, passend zum Abo aus BookingDialogSubscriptions.
     */
    private _getTenantRelationDurationOfStay(assignment: TenantRelationAssignment): number {

        const durationUnit = this._defaultBerthReservationTimeUnit?.type === DEFAULT_BERTH_RESERVATION_UNIT.OVERNIGHT_STAY
            ? DurationUnit.Overnights
            : DurationUnit.Days;

        return getDurationFromDates(
            assignment?.fromDate,
            assignment?.toDate,
            durationUnit,
            this._defaultBerthReservationTimeUnit,
        ) ?? 0;
    }

    private _buildTenantRelationPositions(assignment: TenantRelationAssignment, products?: Product[]): InvoicePosition[] {

        const relationProducts = products ?? assignment?.tenantRelation?.products;

        if (!relationProducts?.length) {
            return [];
        }

        return relationProducts.map(product => {

            const positionDto = {
                product,
                quantity: this.resolveTenantRelationQuantity(assignment, product),
                account: product.account,
                unit: product.unit,
                price: product.price,
                taxRate: product.taxRate,
                fromTenantRelation: true,
                tenantRelationAssignment: assignment,
                tenantRelation: assignment.tenantRelation,
                title: product.name,
                // Leistungszeitraum aus der Beziehung übernehmen, damit er auf Bierdeckel/Bericht befüllt ist
                vestingPeriodFrom: assignment.vestingPeriodFrom ?? assignment.fromDate,
                vestingPeriodUntil: assignment.vestingPeriodUntil ?? assignment.toDate,
            } as InvoicePositionDto;

            return new InvoicePosition(positionDto);
        });
    }

    removeUnsavedFromTenantRelation(): void {
        if (this.bookingDialogService.readonlyView) {
            return;
        }

        // Die Merkliste gehört zur gerade gewählten Beziehung – beim Abräumen muss sie mit weg,
        // sonst legt der nächste Kontextwechsel Positionen einer anderen Beziehung an. (#7017)
        this._unmatchedTenantRelationProductIds.clear();

        this.value = this.value.filter(position => position.id || !position.fromTenantRelation);
    }

    removeFromTenantRelation(): void {
        if (this.bookingDialogService.readonlyView) {
            return;
        }

        this.value
            .filter(position => position.tenantRelationAssignment?.id)
            .forEach(position => {
                position.deleted = true;
            });
    }

    addFromBerthAssignment(assignment: BerthBoatAssignment): void {

        if (this.bookingDialogService.readonlyView) {
            return;
        }

        if (!assignment.berth?.products?.length) {
            return;
        }

        const tenantRelationId = this.bookingDialogService.tenantRelationAssignment.value?.tenantRelation?.id;
        const durationOfStay = this._getDurationOfStay(assignment);

        const positions = filterBerthProductsForBooking(assignment.berth.products, tenantRelationId)
            .map(product => {
                const positionDto = {
                    product,
                    quantity: resolveBerthProductQuantityForBooking(product, durationOfStay),
                    account: product.account,
                    unit: product.unit,
                    price: product.price,
                    taxRate: product.taxRate,
                    fromBerth: true,
                    bcm_berth_has_assignments_id: assignment.id,
                    berthAssignmentUuid: assignment.uuid,
                    title: product.name,
                    // Leistungszeitraum aus dem Belegungszeitraum übernehmen (Bierdeckel/Bericht)
                    vestingPeriodFrom: assignment.from,
                    vestingPeriodUntil: assignment.to,
                } as InvoicePositionDto;

                return new InvoicePosition(positionDto);
            });

        from(positions).pipe(
            concatMap(position => this.getPositionWithDynamicPrice(position))
        ).subscribe({
            next: dynamicPricePosition => {
                this._upsertBerthPosition(dynamicPricePosition);
            }
        });

    }

    removeFromBerthAssignment(assignment: BerthBoatAssignment): void {
        if (this.bookingDialogService.readonlyView) {
            return;
        }

        this.value = this.value.filter(position => position.berthAssignmentUuid !== assignment.uuid);
    }

    addFromElectricMeterAssignment(assignment: ElectricMeterAssignment): void {

        if (this.bookingDialogService.readonlyView) {
            return;
        }

        if (assignment.productType?.key !== ElectricMeterProductKey.BY_USAGE && assignment.product) {

            const translationKeyMapping: Partial<Record<ElectricMeterProductKey, string>> = {
                [ElectricMeterProductKey.PREPAID]: 'electricityForBoatAccordingToPrepaidFee',
                [ElectricMeterProductKey.FLATRATE]: 'electricityForBoatAccordingToFlatrate',
            };

            const translationKey = translationKeyMapping[assignment.productType.key];
            this.composeFromElectricMeterAssignment(
                translationKey,
                assignment,
                assignment.product,
                assignment.quantity,
            );

        } else if (assignment.productType?.key === ElectricMeterProductKey.BY_USAGE && assignment.additionalProduct) {

            this.composeFromElectricMeterAssignment(
                'electricityForBoatAccordingToBasicChargeAdditionalFee',
                assignment,
                assignment.additionalProduct,
                assignment.additionalQuantity,
            );
        }

    }

    composeFromElectricMeterAssignment(translationKey: string, assignment: ElectricMeterAssignment, product: Product, quantity: number) {
        const title = TolgeeService.translate(translationKey,
                {'boatName': (this.bookingDialogService.boat.value?.licensePlate || this.bookingDialogService.boat.value?.name)}) +
            (assignment.electricMeter.cabinet?.handle ? ` Schrank: ${assignment.electricMeter.cabinet.handle} ` : '') +
            (assignment.electricMeter.handle ? `Anschluss: ${assignment.electricMeter.handle} ` : '') +
            (assignment.electricMeter.manufacturerHandle ? `Nr.: ${assignment.electricMeter.manufacturerHandle} ` : '');

        const position = {
            product: product,
            quantity: quantity ?? 1,
            account: product.account,
            unit: product.unit,
            price: product.price,
            taxRate: product.taxRate,
            berthAssignmentUuid: assignment.berthAssignment?.uuid,
            electricMeterAssignmentUuid: assignment.uuid,
            title
        } as InvoicePositionDto;

        this.bookingDialogService.positions.add(new InvoicePosition(position));
    }

    addFromElectricMeterAssignmentDisconnect(event: DisconnectElectricMeterEvent): void {

        if (this.bookingDialogService.readonlyView) {
            return;
        }

        if (!event.product || !event.electricMeterAssignment.productType || event.electricMeterAssignment.productType.key !== ElectricMeterProductKey.BY_USAGE) {
            return;
        }

        const boat = this.bookingDialogService.boat.value;

        const title = TolgeeService.translate('boats.booking.positionElectricityMeterReading', {boatName: (boat.licensePlate || boat.name)}) +
            (event.electricMeterAssignment.electricMeter.cabinet?.handle ? ` Schrank: ${event.electricMeterAssignment.electricMeter.cabinet.handle} ` : '') +
            (event.electricMeterAssignment.electricMeter.handle ? `Anschluss: ${event.electricMeterAssignment.electricMeter.handle} ` : '') +
            (event.electricMeterAssignment.electricMeter.manufacturerHandle ? `Nr.: ${event.electricMeterAssignment.electricMeter.manufacturerHandle} ` : '') +
            `Startablesung: ${toDateStringDE(event.from)} ` +
            `Zählerstand: ${event.meterReadingStart} ` +
            `Schlussablesung: ${toDateStringDE(event.to)} ` +
            `Zählerstand: ${event.meterReadingEnd}`;

        const positionDto = {
            product: event.product,
            quantity: event.quantity ?? 1,
            account: event.product.account,
            unit: event.product.unit,
            price: event.product.price,
            taxRate: event.product.taxRate,
            berthAssignmentUuid: event.electricMeterAssignment.berthAssignment?.uuid,
            electricMeterAssignmentUuid: event.electricMeterAssignment.uuid,
            electricMeterReadingUuid: event.electricMeterAssignment.reading?.uuid,
            title
        } as InvoicePositionDto;

        this.add(new InvoicePosition(positionDto));
    }

    removeFromElectricMeterAssignment(assignment: ElectricMeterAssignment): void {

        if (this.bookingDialogService.readonlyView) {
            return;
        }

        this.value = this.value.filter(position => position.electricMeterAssignmentUuid !== assignment.uuid);
    }

    updateDatesFromBerthAssignments(): void {

        if (this.bookingDialogService.readonlyView) {
            return;
        }

        this.value.forEach(position => {
            if (position.berthAssignmentUuid) {
                const berthAssignment = this.bookingDialogService.berthAssignments.value.find(assignment => assignment.uuid === position.berthAssignmentUuid);
                // Nur mit gesetztem from übernehmen – sonst wirft .toISOString() und bricht die
                // forEach-Datums-Sync für alle Folgepositionen ab.
                if (berthAssignment?.from) {
                    position.vestingPeriodFromDate = berthAssignment.from;
                    position.vestingPeriodFrom = berthAssignment.from.toISOString();
                    position.vestingPeriodUntilDate = berthAssignment.to;
                    position.vestingPeriodUntil = berthAssignment.to?.toISOString();
                }
            } else if (position.fromTenantRelation || position.tenantRelationAssignment) {
                const tenantRelationAssignment = this.bookingDialogService.tenantRelationAssignment.value;
                // Zeitraum nur übernehmen, wenn die Beziehung ihn trägt – sonst wirft .toISOString() auf
                // dem initial null gesetzten Datum und bricht die Datums-Sync aller Folgepositionen ab.
                if (tenantRelationAssignment?.vestingPeriodFrom) {
                    position.vestingPeriodFromDate = tenantRelationAssignment.vestingPeriodFrom;
                    position.vestingPeriodFrom = tenantRelationAssignment.vestingPeriodFrom.toISOString();
                    position.vestingPeriodUntilDate = tenantRelationAssignment.vestingPeriodUntil;
                    position.vestingPeriodUntil = tenantRelationAssignment.vestingPeriodUntil?.toISOString();
                }
            } else if (this.bookingDialogService.booking.from) {
                position.vestingPeriodFromDate = this.bookingDialogService.booking.from;
                position.vestingPeriodFrom = this.bookingDialogService.booking.from.toISOString();
                position.vestingPeriodUntilDate = this.bookingDialogService.booking.to;
                position.vestingPeriodUntil = this.bookingDialogService.booking.to?.toISOString();
            }

            this.getPositionWithDynamicPrice(position)
                .pipe(take(1))
                .subscribe(p => position = p);
        });
    }

    /**
     * Ob die Beschreibung der Position stehen bleiben muss. Sie gehört dem Anwender, sobald sie
     * weder der Produktname noch der zuletzt ausgewertete Regelname ist: `getPositionWithDynamicPrice()`
     * läuft über `updateDatesFromBerthAssignments()` bei jeder Datumsänderung erneut und überschrieb
     * eine über das Stift-Icon geänderte Bezeichnung. Stromzähler-Positionen tragen ihren aus
     * Zählerstand und Schrank zusammengesetzten Text ohnehin selbst. (#6990)
     */
    private _keepsOwnTitle(position: InvoicePosition, previousRuleName?: string): boolean {

        if (position.unit?.uniqueName === UnitUniqueName.KWH && !!position.berthAssignmentUuid) {
            return true;
        }

        const title = position.title;

        return !!title
            && title !== position.product?.name
            && title !== previousRuleName;
    }

    getPositionWithDynamicPrice(position: InvoicePosition): Observable<InvoicePosition> {

        if (position.product.hasDynamicPrice) {

            const boat = this.bookingDialogService.boat.value;
            // Ohne uuid würde .find() die erste Belegung *ohne* uuid treffen und der Position einen
            // fremden Liegeplatz unterschieben – Beziehungspositionen tragen bewusst keine uuid.
            const berth = position.berthAssignmentUuid
                ? this.bookingDialogService.berthAssignments.value.find(assignment => assignment.uuid === position.berthAssignmentUuid)?.berth
                : undefined;
            const tenantRelation = this.bookingDialogService.tenantRelationAssignment.value?.tenantRelation;

            let fromDate: Date | null = null;
            let toDate: Date | null = null;

            if (position.berthAssignmentUuid) {
                const berthAssignment = this.bookingDialogService.berthAssignments.value.find(assignment => assignment.uuid === position.berthAssignmentUuid);
                if (berthAssignment) {
                    fromDate = berthAssignment.from;
                    toDate = berthAssignment.to;
                }
            } else if (position.fromTenantRelation) {
                const tenantRelationAssignment = this.bookingDialogService.tenantRelationAssignment.value;
                if (tenantRelationAssignment) {
                    fromDate = tenantRelationAssignment.fromDate;
                    toDate = tenantRelationAssignment.toDate;
                }
            }

            const days = getDurationFromDates(fromDate, toDate, DurationUnit.Days, this._defaultBerthReservationTimeUnit);
            const overnights = getDurationFromDates(fromDate, toDate, DurationUnit.Overnights, this._defaultBerthReservationTimeUnit);
            const travelers = this.bookingDialogService.travelers.value.length;

            // Entitäten nur mitschicken, wenn sie eine id tragen: ProductService hängt sie sonst als
            // literales `<entity>=undefined` an den Query-String.
            const parameters: Record<string, any> = {days, overnights, travelers};

            if (boat?.id) {
                parameters.boat = boat;
            }

            if (berth?.id) {
                parameters.berth = berth;
            }

            if (tenantRelation?.id) {
                parameters.tenantRelation = tenantRelation;
            }

            return this._productService.evaluatePriceRule(
                position.product as Product,
                parameters,
                position.quantity,
                null,
            )
                .pipe(
                    take(1),
                    map(dynamicPrice => {
                        dynamicPrice = {
                            ...dynamicPrice,
                            // Gespeicherte Zeile behalten: ohne id hielt der Stift-Dialog die Position
                            // für "ohne dyn. Preis" und speicherte den Katalogpreis (#7140).
                            id: position.dynamicPrice?.id,
                            boat,
                            berth,
                            tenantRelation
                        };

                        const previousRuleName = position.dynamicPrice?.ruleName;

                        position.dynamicPrice = dynamicPrice;
                        position.price = dynamicPrice.rulePrice;

                        if (!this._keepsOwnTitle(position, previousRuleName)) {
                            // Ohne Regeltreffer liefert der Evaluator keinen Namen – ohne Fallback
                            // stünde die Position dann ohne Beschreibung da.
                            position.title = dynamicPrice.ruleName || position.product?.name;
                        }

                        return position;
                    })
                );

        } else {
            return of(position);
        }
    }

}
